import Button from 'components/Form/Button';
import Input from 'components/Form/Input';
import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { POST } from 'services/api';
import styled, { keyframes } from 'styled-components';
import { Title } from 'ui/styled';
import { CompanyButtonContainer } from './styled';

// ====== ANIMAÇÃO & ESTILOS ======
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const ModalWrapper = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 10px;
`;

const ModalContainer = styled.div`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2);
  width: 90%;
  max-width: 500px;
  animation: ${fadeIn} 0.3s ease-out;
  padding: 20px;
`;

const HeaderInfo = styled.div`
  margin-bottom: 25px;
  padding-bottom: 15px;
  border-bottom: 1px solid #eee;
`;

const BancoTitle = styled(Title)`
  color: #333;
  font-size: 1.5em;
  font-weight: 600;
  margin-bottom: 5px;
`;

const ReadOnlyField = styled.div`
  width: 100%;
  padding: 12px;
  margin: 8px 0 16px;
  border: 1px solid #e0e0e0;
  background-color: #f9f9f9;
  border-radius: 6px;
  font-size: 1em;
  color: #444;
`;

const Label = styled.label`
  display: block;
  font-weight: 500;
  color: #333;
  margin-bottom: 6px;
  font-size: 0.95em;
`;

const ActionButtonContainer = styled(CompanyButtonContainer)`
  margin-top: 25px;
  justify-content: flex-end;
  gap: 10px;
`;

export default function ModalAcaoEtapa({ proposalData, reload, onClose }) {
  const [loading, setLoading] = useState(false);
  const [dataEnvio, setDataEnvio] = useState('');

  const etapaAtual = useMemo(() => {
    const monitoring = Array.isArray(proposalData?.monitoring)
      ? proposalData.monitoring[0]
      : proposalData?.monitoring;
    return monitoring?.etapa || 'Etapa não disponível';
  }, [proposalData]);

  const bancoNome =
    proposalData?.banco?.nome_fantasia || proposalData?.financial || 'Proposta';
  const statusAtual = proposalData?.status || '';

  // ====== LÓGICA DE CONFIGURAÇÃO DAS ETAPAS ======
  const config = useMemo(() => {
    const etapaLower = etapaAtual.toLowerCase();

    // ===== ENTREGA E PARECER =====
    if (etapaLower.includes('entrega') && etapaLower.includes('parecer')) {
      return {
        titulo: 'Entrega e Parecer de Acesso',
        tipoAcao: 'entrega_parecer',
        statusRequerido: 'Entrega e Parecer de acesso',
        payload: {
          entrega: null,
          curadoria_analise_bot: null,
          data_enviar_recebimento: dataEnvio,
        },
      };
    }

    // ===== PARECER DE ACESSO =====
    if (etapaLower.includes('parecer de acesso')) {
      return {
        titulo: 'Parecer de Acesso',
        tipoAcao: 'parecer_acesso',
        statusRequerido: 'Entrega e Parecer de acesso',
        payload: {
          parecer_acesso: null,
          contato_parecer: null,
          curadoria_analise_bot: null,
          data_enviar_parecer: dataEnvio,
        },
      };
    }

    // ===== PROTOCOLO VISTORIA =====
    if (etapaLower.includes('protocolo')) {
      return {
        titulo: 'Protocolo de Vistoria',
        tipoAcao: 'protocolo_vistoria',
        statusRequerido: 'Instalação e Protocolo de conexão',
        payload: {
          protocolo_acesso: null,
          contato_protocolo: null,
          curadoria_analise_bot: null,
          data_enviar_protocolo: dataEnvio,
        },
      };
    }

    // ===== INSTALAÇÃO =====
    if (etapaLower.includes('instalacao')) {
      return {
        titulo: 'Instalação',
        tipoAcao: 'instalacao',
        statusRequerido: 'Instalação e Protocolo de conexão',
        payload: {
          instalacao: null,
          curadoria_analise_bot: null,
          data_enviar_instalacao: dataEnvio,
        },
      };
    }

    // ===== CONEXÃO =====
    if (etapaLower.includes('conexao')) {
      return {
        titulo: 'Conexão',
        tipoAcao: 'conexao',
        statusRequerido: 'Conexão e 1ª Parcela',
        payload: {
          conexao: null,
          curadoria_analise_bot: null,
          data_enviar_conexao: dataEnvio,
        },
      };
    }

    // ===== PRIMEIRA FATURA =====
    if (etapaLower.includes('primeira fatura')) {
      return {
        titulo: 'Primeira Fatura',
        tipoAcao: 'primeira_fatura',
        statusRequerido: 'Primeira Fatura',
        payload: {
          // primeira_fatura: null,
          curadoria_analise_bot: null,
          data_primeira_fatura: dataEnvio,
        },
      };
    }

    // ===== RECEBIMENTO DE PRODUTOS =====
    if (etapaLower.includes('recebimento produtos')) {
      return {
        titulo: 'Recebimento Produtos',
        tipoAcao: 'data_enviar_recebimento',
        statusRequerido: 'Entrega e Parecer de acesso',
        payload: {
          data_enviar_recebimento: dataEnvio,
        },
      };
    }

    // Fallback
    return {
      titulo: 'Ação Não Suportada',
      tipoAcao: null,
      statusRequerido: null,
      payload: {},
    };
  }, [etapaAtual, dataEnvio]);

  const { titulo, tipoAcao, statusRequerido, payload } = config;
  const podeConfirmar =
    statusAtual === statusRequerido && dataEnvio.trim() !== '';

  const close = () => {
    if (!loading) onClose?.();
  };

  // ===== SALVAR =====
  const save = async () => {
    if (!proposalData?.id) {
      toast.error('Erro: ID da proposta não encontrado.');
      return;
    }

    if (!tipoAcao) {
      toast.error('Ação não suportada para esta etapa.');
      return;
    }

    const payloadFinal = {
      propostaId: proposalData.id,
      tipo_acao: tipoAcao,
      attStatus: 'Analisado',
      data: payload,
    };

    //console.log(`[Payload ${titulo} enviado]:`, payloadFinal);

    setLoading(true);
    try {
      const result = await POST(
        '/financing-desk-control/curadoriareagendar',
        payloadFinal,
        true
      );
      if (result?.status === 'ok' || result?.success) {
        toast.success(`${titulo} enviada com sucesso!`);
        await reload?.();
        close();
      } else {
        toast.error(
          `Erro: ${result?.erro || result?.message || 'Falha inesperada'}`
        );
      }
    } catch (error) {
      console.error(`[Erro na API - ${titulo}]:`, error);
      toast.error('Falha na comunicação com o servidor.');
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <ModalWrapper onClick={close}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <HeaderInfo>
          <BancoTitle>{titulo}</BancoTitle>
          <p style={{ margin: '8px 0', color: '#555' }}>
            Proposta: <strong>{bancoNome}</strong>
          </p>
        </HeaderInfo>
        <div>
          <Label>Etapa Atual</Label>
          <ReadOnlyField>
            <strong>{etapaAtual}</strong>
          </ReadOnlyField>

          {/* <Label>Status Atual</Label>
          <ReadOnlyField>
            <strong>{statusAtual}</strong>
          </ReadOnlyField> */}

          {/* Campo de data (somente data) */}
          <Label>Data *</Label>
          <Input
            type="date"
            value={dataEnvio}
            onChange={(e) => setDataEnvio(e.target.value)}
            style={{ marginBottom: '16px' }}
            required
          />
          {!podeConfirmar && statusRequerido && (
            <div
              style={{
                color: '#d32f2f',
                fontSize: '0.9em',
                marginTop: '16px',
                padding: '12px',
                background: '#ffebee',
                borderRadius: '6px',
                border: '1px solid #ffcdd2',
              }}
            >
              <strong>Atenção: </strong>
              {statusAtual !== statusRequerido ? (
                <>
                  O status precisa estar como <b>{statusRequerido}</b> para
                  enviar.
                </>
              ) : (
                <> Preencha a data corretamente. </>
              )}
            </div>
          )}
        </div>
        {/* ATENÇÃO CENTRALIZADO + TEXTO ABAIXO */}
        <div
          style={{
            marginTop: '16px',
            padding: '14px',
            background: '#ffebee',
            border: '1px solid #ffcdd2',
            borderRadius: '6px',
            textAlign: 'center',
            color: '#d32f2f',
            fontSize: '0.9em',
            lineHeight: '1.6',
          }}
        >
          <div style={{ fontWeight: '700', fontSize: '1em' }}>Atenção</div>
          <div style={{ marginTop: '6px', fontWeight: '500' }}>
            {etapaAtual.toLowerCase().includes('parecer de acesso') ? (
              <> Para o envio da Sol: a cada 5 min (8h–17h, seg–sex) </>
            ) : etapaAtual.toLowerCase().includes('protocolo') ? (
              <> Para o envio da Sol: a cada 5 min (8h–17h, seg–sex) </>
            ) : etapaAtual.toLowerCase().includes('instalacao') ? (
              <>
                Para o envio da Sol: hoje às 9h15 (seg–sex). Se já passou,
                reagende para o próximo dia útil.
              </>
            ) : etapaAtual.toLowerCase().includes('conexao') ? (
              <>
                Para o envio da Sol: hoje às 9h30 (seg–sex). Se já passou,
                reagende para o próximo dia útil.
              </>
            ) : etapaAtual.toLowerCase().includes('primeira fatura') ? (
              <>
                Para o envio da Sol: hoje às 10h (seg–sex). Se já passou,
                reagende para o próximo dia útil.
              </>
            ) : (
              <> Aguardando ação na etapa atual </>
            )}
          </div>
        </div>
        <ActionButtonContainer>
          <Button
            onClick={close}
            color="lightgrey"
            border
            nospace
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            color="blue"
            loading={loading}
            onClick={() => {
              if (!podeConfirmar) {
                if (statusAtual !== statusRequerido) {
                  toast.error(
                    `Não é possível enviar: o status precisa esta correto.`
                  );
                } else if (dataEnvio.trim() === '') {
                  toast.error(
                    'Não é possível enviar: preencha a data corretamente.'
                  );
                }
                return;
              }
              if (tipoAcao) save();
            }}
            nospace
            disabled={loading || !tipoAcao}
          >
            Confirmar
          </Button>
        </ActionButtonContainer>
      </ModalContainer>
    </ModalWrapper>,
    document.body
  );
}
